import { createElement } from "react";
import { ConfirmAlert } from "../components/Alerts/Alerts";
import ConnectionProgressModal from "../components/Containers/ConnectionProgressModal";
import {
  startConnectFacebook,
  startConnectGoogleAds,
  startConnectTiktokBusiness,
  startGetTokenGoogleAds,
  startGetTokenTiktokBusiness,
} from "../actions/user";
import { BUTTON_CONNECT_ACCOUNT } from "../i18n/keysTranslations";
import { GOOGLE, META, TIKTOK } from "../utils/constants";
import {
  AGENCY_LINK_RESUME_QUERY,
  AGENCY_LINKING_PLATFORM,
  toAgencyLinkingPlatform,
} from "../utils/agencyAccountLinking";
import {
  clearPendingAgencyLink,
  peekPendingAgencyLink,
  requestResumePendingLink,
  savePendingAgencyLink,
} from "./session";

const platformConstant = (platform) => {
  switch (platform) {
    case AGENCY_LINKING_PLATFORM.META:
      return META;
    case AGENCY_LINKING_PLATFORM.GOOGLE:
      return GOOGLE;
    case AGENCY_LINKING_PLATFORM.TIKTOK:
      return TIKTOK;
    default: {
      const _unexpected = platform;
      void _unexpected;
      return null;
    }
  }
};

const readPopupCallback = (popup) => {
  try {
    const currentUrl = popup?.location?.href;
    if (!currentUrl) return null;
    const params = new URL(currentUrl).searchParams;
    const code = params.get("code");
    const authCode = params.get("auth_code");
    const state = params.get("state");
    const error = params.get("error");
    const resumeBatchID = params.get(AGENCY_LINK_RESUME_QUERY);
    if (!state || (!code && !authCode && !error)) return null;
    return { code, authCode, state, error, resumeBatchID };
  } catch {
    return null;
  }
};

const watchOAuthPopup = ({ popup, onCallback }) =>
  new Promise((resolve) => {
    const timer = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(timer);
        resolve(null);
        return;
      }
      const callback = readPopupCallback(popup);
      if (!callback) return;
      clearInterval(timer);
      popup.close();
      resolve(callback);
    }, 400);

    if (onCallback) onCallback(() => clearInterval(timer));
  });

const exchangePopupToken = async ({ dispatch, platform, callback }) => {
  if (!callback || callback.error) return false;
  if (platform === AGENCY_LINKING_PLATFORM.GOOGLE) {
    const result = await dispatch(
      startGetTokenGoogleAds({
        authCode: callback.code || callback.authCode,
        state: callback.state,
        withAuth: true,
      })
    );
    return result?.ok === true;
  }
  if (platform === AGENCY_LINKING_PLATFORM.TIKTOK) {
    const result = await dispatch(
      startGetTokenTiktokBusiness({
        authCode: callback.authCode || callback.code,
        state: callback.state,
        withAuth: true,
      })
    );
    return result?.ok === true;
  }
  return false;
};

export const reconnectForAgencyLink = async ({
  dispatch,
  t,
  platform,
  accounts,
  source,
  locale,
  resumeBatchID,
}) => {
  const linkingPlatform = toAgencyLinkingPlatform(platform);
  const confirmPlatform = platformConstant(linkingPlatform);
  if (!linkingPlatform || !confirmPlatform) return false;

  const confirmed = await ConfirmAlert({
    icon: false,
    confirmButtonText: t(BUTTON_CONNECT_ACCOUNT),
    html: createElement(ConnectionProgressModal, { platform: confirmPlatform }),
    width: "60vh",
  });

  if (!confirmed?.isConfirmed) {
    clearPendingAgencyLink();
    return false;
  }

  savePendingAgencyLink({
    accounts,
    source,
    locale,
    resumeBatchID,
    retried: true,
    platform: linkingPlatform,
    expiresAt: Date.now() + 5 * 60 * 1000,
  });

  if (linkingPlatform === AGENCY_LINKING_PLATFORM.META) {
    await dispatch(
      startConnectFacebook({
        onCallbackConnected: () => {
          requestResumePendingLink();
        },
      })
    );
    return true;
  }

  let popup = null;
  const connect =
    linkingPlatform === AGENCY_LINKING_PLATFORM.GOOGLE
      ? startConnectGoogleAds
      : startConnectTiktokBusiness;

  await dispatch(
    connect({
      onChangeLoading: () => {},
      onChangePopupReference: (nextPopup) => {
        popup = nextPopup;
      },
    })
  );

  if (!popup) {
    clearPendingAgencyLink();
    return false;
  }

  const callback = await watchOAuthPopup({ popup });
  if (!callback) {
    clearPendingAgencyLink();
    return false;
  }

  if (callback.resumeBatchID) {
    const pending = peekPendingAgencyLink();
    if (pending) {
      savePendingAgencyLink({
        ...pending,
        resumeBatchID: callback.resumeBatchID,
      });
    }
  }

  const exchanged = await exchangePopupToken({
    dispatch,
    platform: linkingPlatform,
    callback,
  });
  if (!exchanged) {
    clearPendingAgencyLink();
    return false;
  }

  requestResumePendingLink();
  return true;
};
